import React from 'react';
import { createPortal } from 'react-dom';
import { Clock, AlertTriangle, LogOut, RefreshCw } from 'lucide-react';

const SessionTimeoutModal = ({
    isOpen,
    isExpired,
    timeLeft,
    onExtend,
    onLogout
}) => {
    if (!isOpen && !isExpired) return null;
    if (typeof document === 'undefined') return null;

    // Formatting time left
    const formatTime = (seconds) => {
        if (seconds < 0) return "0:00";
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all scale-100">
                {/* Header */}
                <div className={`px-6 py-4 flex items-center justify-between ${isExpired ? 'bg-red-50' : 'bg-orange-50'}`}>
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${isExpired ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'}`}>
                            {isExpired ? <AlertTriangle size={24} /> : <Clock size={24} />}
                        </div>
                        <div>
                            <h3 className={`text-lg font-bold ${isExpired ? 'text-red-900' : 'text-orange-900'}`}>
                                {isExpired ? 'Session Expired' : 'Session Timeout Warning'}
                            </h3>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 text-center">
                    {isExpired ? (
                        <>
                            <p className="text-gray-600 mb-6">
                                Your session has expired due to inactivity. For security reasons, you have been logged out.
                            </p>
                            <p className="text-sm text-gray-500 mb-6">
                                Please log in again to continue your work.
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="text-gray-600 mb-4">
                                Your session will expire in <span className="font-bold text-orange-600 text-xl mx-1">{formatTime(timeLeft)}</span> due to inactivity.
                            </p>
                            <p className="text-sm text-gray-500 mb-6">
                                Would you like to extend your session?
                            </p>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 flex gap-3 justify-end">
                    {isExpired ? (
                        <button
                            onClick={onLogout}
                            className="w-full py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all font-semibold flex items-center justify-center gap-2"
                        >
                            <LogOut size={18} />
                            Go to Login
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={onLogout}
                                className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-semibold flex-1 transition-colors"
                            >
                                Logout Now
                            </button>
                            <button
                                onClick={onExtend}
                                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all font-semibold flex-1 flex items-center justify-center gap-2"
                            >
                                <RefreshCw size={18} />
                                Stay Logged In
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SessionTimeoutModal;
